import * as JSON from 'json-bigint';
import Transaction from './transaction';
import { Saito } from './app';
import Block from './block';
const localforage = require('localforage');
import fs from 'fs';
import path from 'path';
const JsStore = require('jsstore');
import S from 'saito-js/saito';

class Storage {
  public app: Saito;
  public active_tab: any;
  public timeout: any;
  currentBuildNumber: bigint = BigInt(0);
  public localDB: any = null;

  constructor(app) {
    this.app = app || {};
    this.active_tab = 1; // TODO - only active tab saves, move to Browser class
    this.timeout = null;
    this.localDB = null;
  }

  async initialize() {
    await this.loadOptions();

    if (this.app.BROWSER === 0) {
      this.watchBuildFile();
    }

    if (this.app.BROWSER == 1) {
      try {
        this.localDB = null;
        await this.initializeApplicationDB();
      } catch (err) {
        console.log('Error initializeApplicationDB:', err);
      }
    }

    return;
  }

  //
  // Oct 12, 2023 added the safety check from deprecated getOptions()
  // Should we worry about app.BROWSER == 0 ???
  //
  async loadOptions() {
    if (this.app.BROWSER == 1) {
      if (this.active_tab == 0) {
        return;
      }
    }
    const response = await fetch('/options');
    let receivedOptions = await response.json();
    const logPeers = (source: string) => {
      const peers = this.app.options?.peers;
      const n = Array.isArray(peers) ? peers.length : 0;
      const first = n > 0 ? peers[0] : null;
      const wsHint =
        first && first.host != null && first.port != null && first.protocol != null
          ? `${first.protocol === 'https' ? 'wss' : 'ws'}://${first.host}:${first.port}/wsopen`
          : null;
      const hasUrl = !!wsHint;
      const willConnect = n > 0 && hasUrl;
      console.log('[SAITO OPTIONS] peer list after loadOptions', {
        source,
        peerCount: n,
        derivedWebSocketUrl: wsHint,
        hasWebSocketUrl: hasUrl,
        wasmWillAttemptConnect: willConnect,
        firstPeer: first ? { ...first } : null
      });
      if (!willConnect) {
        console.log(
          '[SAITO OPTIONS] WASM will not open outbound sockets (no peers or incomplete host/port/protocol).'
        );
      }
    };
    if (typeof Storage !== 'undefined') {
      const data = localStorage.getItem('options');
      if (data != 'null' && data != null) {
        this.app.options = JSON.parse(data);
        this.app.options.consensus = receivedOptions.consensus;
        this.app.options.defaultModule = receivedOptions.defaultModule;
        // Cached wallet previously only refreshed consensus from the server; peers stayed
        // whatever was in localStorage (often []). Core builds ws://…/wsopen from peers[].
        const cachedPeers = this.app.options.peers;
        const cachedEmpty = !Array.isArray(cachedPeers) || cachedPeers.length === 0;
        const serverPeers = receivedOptions.peers;
        const serverHasPeers = Array.isArray(serverPeers) && serverPeers.length > 0;
        if (cachedEmpty && serverHasPeers) {
          console.log(
            '[SAITO OPTIONS] localStorage had no peers; merging peers[] from GET /options'
          );
          this.app.options.peers = serverPeers;
        }
        await this.readExternalRecords();
        logPeers('localStorage+merge');
        return;
      }
    }
    this.app.options = receivedOptions;
    logPeers('GET_/options_only');
  }

  async readExternalRecords() {
    if (this.app.BROWSER != 1) {
      return;
    }
    const publicKey = this.app.options?.wallet?.publicKey;
    if (!publicKey) {
      return;
    }

    const embeddedGames =
      Array.isArray(this.app.options.games) &&
      this.app.options.games.length > 0 &&
      this.app.options.games[0] &&
      typeof this.app.options.games[0] === 'object';
    const embeddedSaves =
      this.app.options.saves &&
      typeof this.app.options.saves === 'object' &&
      Object.keys(this.app.options.saves).length > 0;

    if (!Array.isArray(this.app.options.external_game_ids) && (embeddedGames || embeddedSaves)) {
      const gameIds = [];
      const writes = [];
      if (embeddedGames) {
        for (let i = 0; i < this.app.options.games.length; i++) {
          const game = this.app.options.games[i];
          if (!game?.id) {
            continue;
          }
          gameIds.push(game.id);
          writes.push(localforage.setItem(`game:${publicKey}:${game.id}`, JSON.stringify(game)));
        }
      }
      const saveIds = [];
      if (embeddedSaves) {
        const saveKeys = Object.keys(this.app.options.saves);
        for (let i = 0; i < saveKeys.length; i++) {
          saveIds.push(saveKeys[i]);
          writes.push(
            localforage.setItem(
              `save:${publicKey}:${saveKeys[i]}`,
              JSON.stringify(this.app.options.saves[saveKeys[i]])
            )
          );
        }
      }
      await Promise.all(writes);
      this.app.options.external_game_ids = gameIds;
      this.app.options.external_save_ids = saveIds;
      this.saveOptions();
      return;
    }

    const gameIds = Array.isArray(this.app.options.external_game_ids)
      ? this.app.options.external_game_ids
      : [];
    const games = [];
    for (let i = 0; i < gameIds.length; i++) {
      const raw = await localforage.getItem(`game:${publicKey}:${gameIds[i]}`);
      if (typeof raw === 'string') {
        try {
          games.push(JSON.parse(raw));
        } catch (err) {}
      } else if (raw && typeof raw === 'object') {
        games.push(raw);
      }
    }
    this.app.options.games = games;

    const saveIds = Array.isArray(this.app.options.external_save_ids)
      ? this.app.options.external_save_ids
      : [];
    const saves = {};
    for (let i = 0; i < saveIds.length; i++) {
      const raw = await localforage.getItem(`save:${publicKey}:${saveIds[i]}`);
      if (typeof raw === 'string') {
        try {
          saves[saveIds[i]] = JSON.parse(raw);
        } catch (err) {}
      } else if (raw && typeof raw === 'object') {
        saves[saveIds[i]] = raw;
      }
    }
    this.app.options.saves = saves;
  }

  returnClientOptions(): string {
    throw new Error('Method not implemented.');
  }

  //
  // HOW THE STORAGE CLASS SAVES TXS
  //
  // modules call ---> app.storage.saveTransaction(tx, obj, peer))
  //    ---> saveTransaction() creates TX type="archive" / request="save" transaction
  //    ---> saveTransaction() directs this transaction to:
  //                    "localhost"   ==> its own archive module
  //      peer    ==> this specific peer via offchain handlePeerTransaction() request)
  //      null    ==> all peers via offchain handlePeerTransaction() request)
  //    ---> peers receive via Archive module
  //    ---> peers save to DB
  //
  // HOW THE STORAGE CLASS LOADS TXS
  //
  // modules call ---> app.storage.loadTransactions()
  //    ---> loadTransactions() creates TX type="archive" / request="load" transaction
  //    ---> loadTransactions() directs this transaction to:
  //                    "localhost"   ==> its own archive module
  //      peer    ==> this specific peer via offchain handlePeerTransaction() request)
  //      null    ==> all peers via offchain handlePeerTransaction() request)
  //    ---> peers receive via Archive module
  //    ---> peers fetch from DB, return via callback or return TX
  //
  async saveTransaction(tx: Transaction, obj = {}, peer = null, blk = null) {
    try {
      const txmsg = tx.returnMessage();
      const message = 'archive';

      let data: any = {};
      data.request = 'save';
      data.serial_transaction = tx.serialize_to_web(this.app);

      data = Object.assign(data, obj);

      //
      // defaults
      //
      // - field1 = module
      // - field2 = sender
      // - field3 = receiver
      //
      if (!data.field1) {
        data.field1 = txmsg.module;
      }
      if (tx.from && tx.from.length > 0) {
        if (!data.field2 && tx.from && tx.from.length > 0) {
          data.field2 = tx.from[0].publicKey;
        }
      }
      if (tx.to && tx.to.length > 0) {
        if (!data.field3 && tx.to && tx.to.length > 0) {
          data.field3 = tx.to[0].publicKey;
        }
      }

      if (blk) {
        data.block_hash = blk.hash;
        data.block_id = Number(blk.id);
      }

      this.app.connection.emit('saito-save-transaction', tx);

      if (peer === 'localhost') {
        let archive_mod = this.app.modules.returnModule('Archive');
        if (archive_mod) {
          return await archive_mod.saveTransaction(tx, data);
        }
      }
      if (peer != null) {
        return await this.app.network.sendRequestAsTransaction(message, data, null, peer.publicKey);
      } else {
        // This await doesn't seem to ever resolve sometimes...
        return await this.app.network.sendRequestAsTransaction(message, data);
      }
    } catch (error) {
      console.warn('failed saving tx : ' + tx.signature);
      console.error(error);
      return { err: error };
    }
    return { err: 'Save Transaction failed' };
  }

  /**
   *
   * Update the DB entry of a transaction, in order to:
   * -- change its meta data (search fields)
   * -- update optional information embedded in the tx
   *
   * updateTransaction will automatically change the timestamp of the update to now(),
   * but you can override this by setting preserve_ts to 1 or providing your desired new timestamp
   * as "updated_at" in the obj
   *
   */
  async updateTransaction(tx: Transaction | null, obj = {}, peer = null, preserve_ts = 0) {
    const message = 'archive';
    let data: any = {};
    data.request = 'update';

    //
    // tx may be null for metadata-only Archive updates (e.g. owner change).
    // Localhost forwards (tx, obj) directly to Archive.updateTransaction.
    //
    if (tx) {
      data.optional = tx.optional;
      data.serial_transaction = tx.serialize_to_web(this.app);

      if (!(obj as any)['updated_at']) {
        if (preserve_ts) {
          (obj as any)['updated_at'] = tx.optional.updated_at || tx.timestamp;
        }
      }
    }

    data = Object.assign(data, obj);

    if (peer === 'localhost') {
      let archive_mod = this.app.modules.returnModule('Archive');
      if (archive_mod) {
        return await archive_mod.updateTransaction(tx, obj);
      }
    } else {
      if (peer != null) {
        return await this.app.network.sendRequestAsTransaction(message, data, null, peer.publicKey);
      } else {
        return await this.app.network.sendRequestAsTransaction(message, data);
      }
    }
    return { err: 'Save Transaction failed' };
  }

  /**
   *
   * @param obj : search criteria corresponding to archive fields
   * @param mycallback : function to run on the returned data
   * @param peer : "localhost", null, or Peer to load transactions from
   * @param deserialize: flag to run the deserialize function and return the transactions as transactions (default)
   *
   * Note: You might need to await this function for the internal callbacks to work...
   */
  async loadTransactions(obj = {}, mycallback, peer = null, deserialize = 1) {
    let storage_self = this;
    const message = 'archive';
    let data: any = {};
    data.request = 'load';
    data = Object.assign(data, obj);
    const startTime = Date.now();

    let internal_callback = (res) => {
      let txs = [];
      const endTime = Date.now();
      if (res) {
        for (let i = 0; i < res.length; i++) {
          if (res[i]?.tx) {
            if (deserialize) {
              let tx = new Transaction();
              tx.deserialize_from_web(storage_self.app, res[i].tx);
              if (!tx.optional.updated_at) {
                // Backward compatibility
                tx.optional.updated_at = res[i].updated_at;
              }
              txs.push(tx);
            } else {
              txs.push(res[i].tx);
            }
          }
        }
      }
      if (peer?.publicKey) {
        console.debug(`>>> Transction fetch elapsed time: ${endTime - startTime}ms`);
      }
      return mycallback(txs);
    };

    if (peer === 'localhost') {
      let archive_mod = this.app.modules.returnModule('Archive');
      if (archive_mod) {
        return archive_mod.loadTransactionsWithCallback(obj, (res) => {
          return internal_callback(res);
        });
      }
    }

    if (peer != null) {
      this.app.network.sendRequestAsTransaction(
        message,
        data,
        (res) => {
          return internal_callback(res);
        },
        peer.publicKey
      );
    } else {
      this.app.network.sendRequestAsTransaction(message, data, function (res) {
        return internal_callback(res);
      });
    }

    return [];
  }

  async deleteTransaction(tx = null, mycallback = null, peer = null) {
    if (tx == null) {
      return;
    }

    const message = 'archive';
    let data: any = {};
    data.request = 'delete';

    if (peer === 'localhost') {
      let archive_mod = this.app.modules.returnModule('Archive');
      if (archive_mod) {
        await archive_mod.deleteTransaction(tx);
      }
      return;
    }

    if (peer != null) {
      this.app.network.sendRequestAsTransaction(message, data, function () {
        if (mycallback != null) {
          mycallback();
        }
      });
    }
  }

  async deleteTransactions(obj = {}, mycallback = null, peer = null) {
    const message = 'archive';
    let data: any = {};
    data.request = 'multidelete';
    data = Object.assign(data, obj);

    if (peer === 'localhost') {
      let archive_mod = this.app.modules.returnModule('Archive');
      if (archive_mod) {
        await archive_mod.deleteTransactions(obj);
        if (mycallback != null) {
          mycallback();
        }
      }
      return;
    }

    if (peer != null) {
      this.app.network.sendRequestAsTransaction(message, data, function (obj) {
        if (mycallback != null) {
          mycallback();
        }
      });
    }
  }

  async resetOptions() {
    try {
      //Wipe local storage before resaving options
      localStorage.clear();

      const response = await fetch(`/options`);
      this.app.options = await response.json();
      this.saveOptions();
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Load pristine /options without clearing storage.
   * Used after resetBrowserInstallation() has already wiped local state.
   */
  async loadFreshOptions() {
    if (!this.app.BROWSER) {
      return;
    }
    try {
      const response = await fetch(`/options`);
      this.app.options = await response.json();
      this.saveOptions();
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Best-effort deletion of every IndexedDB database in this origin.
   * Falls back to known Saito DB names when indexedDB.databases() is unavailable.
   */
  async deleteAllIndexedDatabases() {
    if (!this.app.BROWSER || typeof indexedDB === 'undefined') {
      return;
    }

    try {
      if (this.localDB) {
        try {
          await this.localDB.dropDb();
        } catch (err) {
          // Connection may already be closed or DB already gone.
        }
        this.localDB = null;
      }

      const knownNames = [
        'localforage',
        'keyvaluepairs',
        'archive_db',
        'dyn_mods_db',
        'popup',
        'emoji-picker-element-en',
        'N64WASMDB'
      ];

      let names = knownNames;
      if (typeof indexedDB.databases === 'function') {
        try {
          const dbs = await indexedDB.databases();
          const discovered = (dbs || []).map((db) => db?.name).filter(Boolean);
          names = Array.from(new Set([...knownNames, ...discovered]));
        } catch (err) {
          // Safari / older browsers may throw; keep knownNames fallback.
        }
      }

      await Promise.all(names.map((name) => this.deleteIndexedDatabase(name)));
    } catch (err) {
      console.error('deleteAllIndexedDatabases:', err);
    }
  }

  deleteIndexedDatabase(name) {
    return new Promise((resolve) => {
      if (!name) {
        resolve(null);
        return;
      }
      try {
        const req = indexedDB.deleteDatabase(name);
        req.onsuccess = () => resolve(null);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch (err) {
        resolve(null);
      }
    });
  }

  async clearCacheStorage() {
    if (!this.app.BROWSER || typeof caches === 'undefined') {
      return;
    }
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (err) {
      console.error('clearCacheStorage:', err);
    }
  }

  async unregisterServiceWorkers() {
    if (!this.app.BROWSER || !('serviceWorker' in navigator)) {
      return;
    }
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch (err) {
      console.error('unregisterServiceWorkers:', err);
    }
  }

  /**
   * Wipe all locally persisted browser state so the origin behaves like a
   * brand-new Saito install. Does not create a wallet — callers reload or
   * re-init afterward.
   *
   * Re-opens the dyn_mods JsStore handle after the wipe so dynamic-module
   * install still works in the same session (e.g. first-wallet resetWallet).
   */
  async resetBrowserInstallation() {
    if (!this.app.BROWSER) {
      return;
    }

    await this.clearLocalForage();
    await this.removeAllLocalApplications();
    await this.deleteAllIndexedDatabases();

    try {
      sessionStorage.clear();
    } catch (err) {
      console.error('sessionStorage.clear:', err);
    }

    try {
      localStorage.clear();
    } catch (err) {
      console.error('localStorage.clear:', err);
    }

    await this.clearCacheStorage();
    await this.unregisterServiceWorkers();

    try {
      await this.initializeApplicationDB();
    } catch (err) {
      console.log('Error initializeApplicationDB after resetBrowserInstallation:', err);
    }
  }

  async resetOptionsFromKey(publicKey) {
    if (this.app.BROWSER) {
      let wallet = await localforage.getItem(publicKey);
      if (wallet) {
        console.log(`Found wallet for ${publicKey} in IndexedDB`);
        //siteMessage(`Found wallet for ${publicKey} in IndexedDB`);
        this.app.options = wallet;
        await this.readExternalRecords();
        this.app.storage.saveOptions();
      } else {
        console.log(`Creating fresh wallet for ${publicKey}`);
        //siteMessage(`Creating fresh wallet for ${publicKey}`);
        await this.resetOptions();
      }
    }
  }

  /**
   *  Save the entire app.options as a key-entry pair in localForage -- enables switching accounts
   *
   */
  async saveOptionsToForage() {
    if (this.app.BROWSER) {
      let key = await this.app.wallet.getPublicKey();
      if (key) {
        const shell = { ...this.app.options };
        delete shell.games;
        delete shell.saves;
        localforage.setItem(key, shell);
      }
    }
  }

  /**
   * Wrapper classes so modules can consistently use localForage (indexedDB)
   */

  async getLocalForageItem(key) {
    if (this.app.BROWSER) {
      try {
        return await localforage.getItem(key);
      } catch (err) {
        console.error(err);
        return null;
      }
    }
  }

  async setLocalForageItem(key, value) {
    if (this.app.BROWSER) {
      try {
        return await localforage.setItem(key, value);
      } catch (err) {
        console.error(err);
        return null;
      }
    }
  }

  async removeLocalForageItem(key) {
    if (this.app.BROWSER) {
      try {
        return await localforage.removeItem(key);
      } catch (err) {
        console.error(err);
        return null;
      }
    }
  }

  async clearLocalForage() {
    if (this.app.BROWSER) {
      try {
        await localforage.clear();
        console.log('Cleared LocalForage!');
      } catch (err) {
        console.error(err);
      }
    }
  }

  saveOptions(replacements: Array<{ path: Array<string | number>; json: string }> = []) {
    if (this.app.BROWSER == 1) {
      if (this.active_tab == 0) {
        return;
      }
    }

    // A replacement is an already-serialized value somewhere in app.options.
    // The live object is swapped for a token while stringify runs, then the
    // token is replaced with that JSON so the subtree is not walked again.
    // games and saves are not part of the localStorage wallet; they are written
    // as one IndexedDB record each. A replacement whose path starts at games
    // supplies the JSON for that record.
    const publicKey = this.app.options?.wallet?.publicKey;
    const externalize = this.app.BROWSER == 1 && !!publicKey;
    const previousGameIds = Array.isArray(this.app.options?.external_game_ids)
      ? this.app.options.external_game_ids.slice()
      : [];
    const previousSaveIds = Array.isArray(this.app.options?.external_save_ids)
      ? this.app.options.external_save_ids.slice()
      : [];
    const gameJsonById = {};
    const applied = [];
    let new_wallet_json = '';

    try {
      for (let i = 0; i < replacements.length; i++) {
        const item = replacements[i];
        if (
          !item ||
          !Array.isArray(item.path) ||
          item.path.length === 0 ||
          typeof item.json !== 'string'
        ) {
          continue;
        }
        if (externalize && item.path[0] === 'games') {
          const game = this.app.options.games?.[item.path[1]];
          if (game?.id) {
            gameJsonById[game.id] = item.json;
          }
          continue;
        }
        if (externalize && item.path[0] === 'saves') {
          continue;
        }

        let parent = this.app.options;
        for (let p = 0; p < item.path.length - 1; p++) {
          parent = parent?.[item.path[p]];
        }
        if (parent == null) {
          continue;
        }

        const key = item.path[item.path.length - 1];
        const token = `__SAITO_SAVED_${i}_${Date.now()}_${Math.random().toString(36).slice(2)}__`;
        applied.push({ parent, key, original: parent[key], token, json: item.json });
        parent[key] = token;
      }

      if (externalize) {
        const games = Array.isArray(this.app.options.games) ? this.app.options.games : [];
        const saves =
          this.app.options.saves && typeof this.app.options.saves === 'object'
            ? this.app.options.saves
            : {};
        const currentGameIds = [];
        for (let i = 0; i < games.length; i++) {
          if (games[i]?.id) {
            currentGameIds.push(games[i].id);
          }
        }
        this.app.options.external_game_ids = currentGameIds;
        this.app.options.external_save_ids = Object.keys(saves);
      }

      if (externalize) {
        const shell = { ...this.app.options };
        delete shell.games;
        delete shell.saves;
        new_wallet_json = JSON.stringify(shell);
      } else {
        new_wallet_json = JSON.stringify(this.app.options);
      }
      for (let i = 0; i < applied.length; i++) {
        new_wallet_json = new_wallet_json
          .split(JSON.stringify(applied[i].token))
          .join(applied[i].json);
      }
    } finally {
      for (let i = applied.length - 1; i >= 0; i--) {
        applied[i].parent[applied[i].key] = applied[i].original;
      }
    }

    if (externalize) {
      const games = Array.isArray(this.app.options.games) ? this.app.options.games : [];
      const saves =
        this.app.options.saves && typeof this.app.options.saves === 'object'
          ? this.app.options.saves
          : {};
      const currentGameIds = Array.isArray(this.app.options.external_game_ids)
        ? this.app.options.external_game_ids
        : [];
      const currentSaveIds = Array.isArray(this.app.options.external_save_ids)
        ? this.app.options.external_save_ids
        : [];

      for (let i = 0; i < games.length; i++) {
        const id = games[i]?.id;
        if (!id) {
          continue;
        }
        const body = gameJsonById[id] || JSON.stringify(games[i]);
        localforage.setItem(`game:${publicKey}:${id}`, body).catch((err) => {
          console.error(err);
        });
      }
      for (let i = 0; i < previousGameIds.length; i++) {
        if (currentGameIds.indexOf(previousGameIds[i]) === -1) {
          localforage.removeItem(`game:${publicKey}:${previousGameIds[i]}`).catch((err) => {
            console.error(err);
          });
        }
      }

      for (let i = 0; i < currentSaveIds.length; i++) {
        const id = currentSaveIds[i];
        localforage.setItem(`save:${publicKey}:${id}`, JSON.stringify(saves[id])).catch((err) => {
          console.error(err);
        });
      }
      for (let i = 0; i < previousSaveIds.length; i++) {
        if (currentSaveIds.indexOf(previousSaveIds[i]) === -1) {
          localforage.removeItem(`save:${publicKey}:${previousSaveIds[i]}`).catch((err) => {
            console.error(err);
          });
        }
      }
    }

    try {
      localStorage.setItem('options', new_wallet_json);
    } catch (err) {
      for (let i = 0; i < localStorage.length; i++) {
        let item = localStorage.getItem(localStorage.key(i));
        let parsed_item = '';
        try {
          parsed_item = JSON.parse(item);
        } catch (err) {
          // Not everything is json... we don't care
        }
        // console.debug(localStorage.key(i), item.length, item, parsed_item);
      }
      // console.debug(`Trying to save: (${new_wallet_json.length})`, JSON.parse(new_wallet_json));
      for (let key in this.app.options) {
        // console.log(key, JSON.stringify(this.app.options[key]).length);
      }
    }

    try {
      //update indexedDB (which is needed for privateKey wallet recovery)
      this.saveOptionsToForage();
    } catch (err) {
      // console.error('LocalForage error: ', err);
    }
  }

  async installLocalApplication(mod, bin, nft_id = '', nft_tx_sig = '') {
    if (!this.app.BROWSER) {
      return;
    }

    if (this.app.BROWSER) {
      let obj = {
        mod: mod,
        binary: bin,
        nft_id: nft_id || '',
        nft_tx_sig: nft_tx_sig || '',
        created_at: new Date().getTime(),
        updated_at: new Date().getTime()
      };

      // console.log('obj: ', obj);

      let numRows = await this.localDB.insert({
        into: 'dyn_mods',
        values: [obj]
      });

      let v = await this.loadLocalApplications();
      // console.log('POST INSERT: ' + JSON.stringify(v));
    }
  }

  async loadLocalApplications(mod_slug = null) {
    try {
      if (!this.app.BROWSER) {
        return;
      }

      let obj = {
        from: 'dyn_mods',
        order: { by: 'id', type: 'desc' }
      };

      if (mod_slug != null) {
        obj['where'] = {
          mod: mod_slug
        };
      }

      let rows = await this.localDB.select(obj);

      return rows;
    } catch (err) {
      console.log('Error loadLocalApplications: ', err);
    }
  }

  async uninstallLocalApplication(mod_slug = null, nft_tx_sig = null) {
    try {
      if (!this.app.BROWSER) {
        return;
      }

      const where = nft_tx_sig ? { nft_tx_sig: nft_tx_sig } : { mod: mod_slug };

      let rowsDeleted = await this.localDB.remove({
        from: 'dyn_mods',
        where
      });

      return rowsDeleted;
    } catch (err) {
      console.log('Error removeLocalApplication: ', err);
    }
  }

  async removeAllLocalApplications() {
    try {
      if (!this.app.BROWSER) {
        return;
      }

      let rowsDeleted = await this.localDB.remove({
        from: 'dyn_mods'
      });

      return rowsDeleted;
    } catch (err) {
      console.log('Error removeLocalApplication: ', err);
    }
  }

  async initializeApplicationDB() {
    if (this.app.BROWSER) {
      this.localDB = new JsStore.Connection(new Worker('/saito/lib/jsstore/jsstore.worker.js'));

      //
      // create Local database
      //
      let dyn_mod = {
        name: 'dyn_mods',
        columns: {
          id: { primaryKey: true, autoIncrement: true },
          mod: { dataType: 'string', default: '' },
          binary: { dataType: 'string', default: '' },
          nft_id: { dataType: 'string', default: '' },
          nft_tx_sig: { dataType: 'string', default: '' },
          created_at: { dataType: 'number', default: 0 },
          updated_at: { dataType: 'number', default: 0 }
        }
      };

      let db = {
        name: 'dyn_mods_db',
        tables: [dyn_mod]
      };

      var isDbCreated = await this.localDB.initDb(db);
      if (isDbCreated) {
        console.log('STORAGE: db created and connection opened');
      } else {
        console.log('STORAGE: connection opened');
      }
    }

    return;
  }

  getModuleOptionsByName(modname) {
    for (let i = 0; i < this.app.options.modules.length; i++) {
      if (this.app.options.modules[i].name === modname) {
        return this.app.options.modules[i];
      }
    }
    return null;
  }

  /**
   * DUMMY FUNCTIONS IMPLEMENTED BY STORAGE-CORE IN ./core/storage-core.js
   **/
  deleteBlockFromDisk(filename) {}

  async loadBlockById(bid): Promise<Block> {
    return null;
  }

  async loadBlockByHash(bsh): Promise<Block> {
    return null;
  }

  async loadBlockFromDisk(filename): Promise<Block> {
    return null;
  }

  async loadBlockByFilename(filename): Promise<Block> {
    return null;
  }

  async loadBlocksFromDisk(maxblocks = 0): Promise<Block> {
    return null;
  }

  returnPath() {
    return null;
  }

  returnFileSystem() {
    return null;
  }

  async saveBlock(block: Block): Promise<string> {
    return '';
  }

  saveClientOptions() {}

  async returnDatabaseByName(dbname) {
    return null;
  }

  async returnBlockFilenameByHash(block_hash, mycallback) {}

  returnTokenSupplySlipsFromDisk(): any {
    return [];
  }

  returnBlockFilenameByHashPromise(block_hash: string) {}

  async queryDatabase(sql, params, database) {}

  async runDatabase(sql, params, database, mycallback = null) {}

  async executeDatabase(sql, database) {}

  generateBlockFilename(block: Block): string {
    return ''; // empty
  }

  watchBuildFile(): void {
    const checkBuildNumber = async () => {
      const filePath = path.join(__dirname, 'dist/build.json');
      fs.readFile('dist/build.json', 'utf8', async (err, data) => {
        if (err) {
          console.error('Error reading options file:', err);
          return;
        }
        try {
          const jsonData = JSON.parse(data);
          const buildNumber = BigInt(jsonData.build_number);
          if (typeof this.currentBuildNumber == 'undefined') {
            console.info('Build number undefined');
            return false;
          }
          if (typeof buildNumber == 'undefined') {
            console.info('Error reading build number from file');
            return false;
          }
          if (Number(this.currentBuildNumber) < Number(buildNumber)) {
            let buffer = { buildNumber };
            let jsonString = JSON.stringify(buffer);
            let uint8Array = new Uint8Array(jsonString.length);
            for (let i = 0; i < jsonString.length; i++) {
              uint8Array[i] = jsonString.charCodeAt(i);
            }
            this.app.build_number = Number(buildNumber);
            let peers = await this.app.network.getPeers();
            // console.log('peers', peers);
            peers.forEach((peer) => {
              this.app.network.sendRequest('software-update', data, null, peer);
            });

            this.currentBuildNumber = buildNumber;

            console.log('Updated build number to:', this.currentBuildNumber);
          } else {
            // console.log("Current build number is up-to-date or higher");
          }
        } catch (e) {
          // console.error('Error parsing JSON from options file:', e);
        }
      });
    };

    fs.watchFile('web/saito/saito.js', { interval: 1000 }, (curr, prev) => {
      checkBuildNumber();
    });

    const filePath = path.join(__dirname, 'dist/build.json');
  }

  async loadNFTTransactions(nft_id) {
    //
    // load NFT transaction from local archive first
    //
    let nfttx = await new Promise((resolve) => {
      this.app.storage.loadTransactions(
        { field4: nft_id },
        (txs) => {
          if (Array.isArray(txs) && txs.length > 0) {
            resolve(txs);
            return;
          }
          resolve(null);
        },
        'localhost'
      );
    });

    if (nfttx) return nfttx;

    //
    // load NFT transaction from remote peers
    // if local not found
    //
    const peers = await this.app.network.getPeers();

    if (peers?.length > 0) {
      nfttx = await new Promise((resolve) => {
        this.app.storage.loadTransactions(
          { field4: nft_id },
          (txs) => {
            if (Array.isArray(txs) && txs.length > 0) {
              resolve(txs);
              return;
            }
            resolve(null);
          },
          peers[0]
        );
      });

      if (nfttx) return nfttx;
    }

    return nfttx;
  }
}

export default Storage;
